import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Edit, Eye, X, Package,
  Search, ChevronRight, ChevronDown, Folder, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import predefineTemplateService from '../services/predefineTemplateService';
import { CreateButton, SaveButton, CancelButton } from '../components/CommonButtons';
import ResponsiveModal from '../components/ResponsiveModal';
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import { FormLoader } from '../components/CommonLoader';
import { useDebounce } from '../hooks/useDebounce';
import Switch from '../components/Switch';

const PredefineTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(null); // Track which template is being toggled
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [filterActive, setFilterActive] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    folder_structure: [],
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Folder structure builder states
  const [folderStructure, setFolderStructure] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [expandedPreviewFolders, setExpandedPreviewFolders] = useState(new Set());
  const [newFolderInputs, setNewFolderInputs] = useState({}); // Track input values for each level

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await predefineTemplateService.getTemplates({
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchTerm,
        is_active: filterActive === '' ? undefined : (filterActive === 'true' ? 1 : 0),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      const data = response.data || response;
      console.log('Fetch templates response:', data);
      const templatesArray = data.templates || data.data || [];
      console.log('Templates array:', templatesArray);
      setTemplates(templatesArray);

      if (data.pagination) {
        setTotalPages(data.pagination.last_page || 1);
        setTotalRecords(data.pagination.total || 0);
        setCurrentPage(data.pagination.current_page || 1);
      } else {
        setTotalPages(data.last_page || Math.ceil((data.total || 0) / perPage));
        setTotalRecords(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to fetch templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, perPage, filterActive, sortBy, sortOrder]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Count folders recursively
  const countFoldersRecursive = (folders) => {
    if (!folders || !Array.isArray(folders)) return 0;
    let count = folders.length;
    folders.forEach(folder => {
      if (folder.children && Array.isArray(folder.children)) {
        count += countFoldersRecursive(folder.children);
      }
    });
    return count;
  };

  // Handle create new template
  const handleCreateTemplate = () => {
    setFormData({
      name: '',
      description: '',
      folder_structure: [],
      is_active: true
    });
    setFolderStructure([]);
    setFormErrors({});
    setExpandedFolders(new Set());
    setNewFolderInputs({});
    setSelectedTemplate(null);
    setShowCreateModal(true);
  };

  // Handle edit template
  const handleEditTemplate = async (template) => {
    console.log('Edit template data:', template);

    try {
      // Always fetch full template details to ensure we have the complete folder structure
      const fullTemplate = await predefineTemplateService.getTemplatePreview(template.id);
      console.log('Full template data for edit:', fullTemplate);

      setSelectedTemplate(fullTemplate);

      // Use folders array directly from API (with nested children structure)
      const folders = fullTemplate.folders || fullTemplate.folder_structure || [];
      console.log('Setting folder structure for edit:', JSON.stringify(folders, null, 2));
      console.log('Setting form data with name:', fullTemplate.name, 'description:', fullTemplate.description);

      // Update both formData and folderStructure
      const newFormData = {
        name: fullTemplate.name || '',
        description: fullTemplate.description || '',
        folder_structure: folders,
        is_active: fullTemplate.is_active === true || fullTemplate.is_active === 1
      };

      console.log('New form data:', newFormData);
      setFormData(newFormData);
      setFolderStructure(folders);
      setFormErrors({});

      // Initialize some folders as expanded to show structure
      const initialExpanded = new Set();
      if (folders.length > 0 && folders[0].children && folders[0].children.length > 0) {
        initialExpanded.add('0'); // Expand first folder if it has children
      }
      setExpandedFolders(initialExpanded);
      setNewFolderInputs({});
      setShowEditModal(true);
    } catch (error) {
      console.error('Failed to load template for editing:', error);
      toast.error('Failed to load template details');
    }
  };

  // Handle preview template
  const handlePreviewTemplate = async (template) => {
    try {
      const data = await predefineTemplateService.getTemplatePreview(template.id);
      console.log('Full template data for preview:', data);

      // Use folders array directly from API (with nested children structure)
      const folders = data.folders || data.folder_structure || [];
      console.log('Setting folder structure for preview:', JSON.stringify(folders, null, 2));
      console.log('Folders array length:', folders.length);

      setSelectedTemplate(data);
      setFolderStructure(folders);

      // Initialize some folders as expanded to show structure
      const initialExpanded = new Set();
      if (folders.length > 0) {
        initialExpanded.add('0'); // Expand first folder
        if (folders[0].children && folders[0].children.length > 0) {
          folders[0].children.forEach((child, index) => {
            initialExpanded.add(`0-${index}`); // Expand children of first folder
          });
        }
      }
      console.log('Initial expanded folders:', Array.from(initialExpanded));
      setExpandedPreviewFolders(initialExpanded);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Failed to fetch template preview:', error);
      toast.error('Failed to fetch template preview');
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) {
      errors.name = 'Template name is required';
    }
    return errors;
  };


  // Save template
  const handleSaveTemplate = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        name: formData.name,
        description: formData.description,
        is_active: formData.is_active,
        folder_structure: folderStructure  // Send the nested structure as is
      };

      console.log('Saving template with data:', dataToSave);

      if (showEditModal && selectedTemplate) {
        await predefineTemplateService.updateTemplate(selectedTemplate.id, dataToSave);
        toast.success('Template updated successfully');
      } else {
        await predefineTemplateService.createTemplate(dataToSave);
        toast.success('Template created successfully');
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(`Failed to save template: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle template status
  const handleToggleStatus = async (template) => {
    try {
      setTogglingStatus(template.id);
      await predefineTemplateService.toggleTemplateStatus(template.id);
      // Show success message - template.is_active has the OLD status, so we reverse it
      const newStatus = !template.is_active;
      toast.success(`Template ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to toggle template status:', error);
      toast.error('Failed to toggle template status');
    } finally {
      setTogglingStatus(null);
    }
  };


  // Add folder to root level
  const addRootFolder = () => {
    const rootInput = newFolderInputs['root'] || '';
    if (!rootInput.trim()) {
      toast.error('Folder name is required');
      return;
    }

    const newFolder = {
      name: rootInput.trim(),
      children: []
    };

    setFolderStructure([...folderStructure, newFolder]);
    setNewFolderInputs({ ...newFolderInputs, root: '' });
    toast.success('Folder added successfully');
  };

  // Add folder to specific path
  const addFolderToPath = (parentPath) => {
    const pathKey = parentPath.join('-');
    const folderName = newFolderInputs[pathKey] || '';

    if (!folderName.trim()) {
      toast.error('Folder name is required');
      return;
    }

    const newFolder = {
      name: folderName.trim(),
      children: []
    };

    const updatedStructure = JSON.parse(JSON.stringify(folderStructure));
    let current = updatedStructure;

    // Navigate to the target folder
    for (let i = 0; i < parentPath.length; i++) {
      const index = parentPath[i];
      if (i === parentPath.length - 1) {
        // This is the target parent folder
        if (!current[index].children) {
          current[index].children = [];
        }
        current[index].children.push(newFolder);
      } else {
        current = current[index].children;
      }
    }

    setFolderStructure(updatedStructure);
    setNewFolderInputs({ ...newFolderInputs, [pathKey]: '' });
    toast.success('Folder added successfully');
  };

  // Remove folder from structure
  const removeFolder = (pathIndices) => {
    const updatedStructure = JSON.parse(JSON.stringify(folderStructure));

    if (pathIndices.length === 1) {
      // Remove from root
      updatedStructure.splice(pathIndices[0], 1);
    } else {
      // Navigate to parent and remove
      let parent = updatedStructure;
      for (let i = 0; i < pathIndices.length - 1; i++) {
        if (i === pathIndices.length - 2) {
          // Parent folder found
          parent[pathIndices[i]].children.splice(pathIndices[pathIndices.length - 1], 1);
        } else {
          parent = parent[pathIndices[i]].children;
        }
      }
    }

    setFolderStructure(updatedStructure);
    toast.success('Folder removed successfully');
  };

  // Toggle folder expansion
  const toggleFolderExpansion = (folderPath) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  // Toggle preview folder expansion
  const togglePreviewFolderExpansion = (folderPath) => {
    const newExpanded = new Set(expandedPreviewFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedPreviewFolders(newExpanded);
  };

  // Update new folder input
  const updateNewFolderInput = (pathKey, value) => {
    setNewFolderInputs({ ...newFolderInputs, [pathKey]: value });
  };

  // Render improved folder structure with inline add functionality
  const renderImprovedFolderStructure = (folders, parentPath = [], level = 0) => {
    console.log('Rendering folders at level', level, ':', folders);
    if (!folders || !Array.isArray(folders) || folders.length === 0) {
      return null;
    }

    return (
      <div className={level === 0 ? '' : 'ml-6'}>
        {folders.map((folder, index) => {
          const currentPath = [...parentPath, index];
          const pathStr = currentPath.join('-');
          const isExpanded = expandedFolders.has(pathStr);
          const hasChildren = folder.children && folder.children.length > 0;

          return (
            <div key={pathStr} className="mb-2">
              {/* Folder Row */}
              <div className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                <button
                  onClick={() => toggleFolderExpansion(pathStr)}
                  className="p-0.5 hover:bg-gray-100 rounded"
                  type="button"
                >
                  {hasChildren || newFolderInputs[pathStr] !== undefined ? (
                    isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  ) : (
                    <div className="w-4" />
                  )}
                </button>
                <Folder size={16} className="text-yellow-500" />
                <span className="flex-1 font-medium">{folder.name}</span>

                {/* Add and Delete buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      const pathKey = currentPath.join('-');
                      if (!newFolderInputs[pathKey]) {
                        setNewFolderInputs({ ...newFolderInputs, [pathKey]: '' });
                        if (!isExpanded) {
                          toggleFolderExpansion(pathStr);
                        }
                      }
                    }}
                    className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                    title="Add subfolder"
                    type="button"
                  >
                    <Plus size={14} />
                  </button>
                  <button
                    onClick={() => removeFolder(currentPath)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Remove folder"
                    type="button"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Expanded content or add input */}
              {(isExpanded || newFolderInputs[pathStr] !== undefined) && (
                <div className="ml-6 mt-2">
                  {/* Add new folder input */}
                  {newFolderInputs[pathStr] !== undefined && (
                    <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 rounded border border-blue-200">
                      <Folder size={16} className="text-blue-500" />
                      <input
                        type="text"
                        value={newFolderInputs[pathStr] || ''}
                        onChange={(e) => updateNewFolderInput(pathStr, e.target.value)}
                        placeholder="New folder name"
                        className="flex-1 p-1 border rounded text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addFolderToPath(currentPath);
                          }
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => addFolderToPath(currentPath)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        type="button"
                      >
                        <CheckCircle size={14} />
                      </button>
                      <button
                        onClick={() => {
                          const newInputs = { ...newFolderInputs };
                          delete newInputs[pathStr];
                          setNewFolderInputs(newInputs);
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}

                  {/* Children folders */}
                  {hasChildren && renderImprovedFolderStructure(folder.children, currentPath, level + 1)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render folder structure for preview (read-only)
  const renderPreviewFolderStructure = (folders, parentPath = [], level = 0) => {
    console.log('Rendering preview folders at level', level, ':', folders);
    if (!folders || !Array.isArray(folders) || folders.length === 0) {
      return <p className="text-gray-500">No folders to display</p>;
    }

    return (
      <div className={level === 0 ? '' : 'ml-6'}>
        {folders.map((folder, index) => {
          const currentPath = [...parentPath, index];
          const pathStr = currentPath.join('-');
          const isExpanded = expandedPreviewFolders.has(pathStr);
          const hasChildren = folder.children && folder.children.length > 0;

          return (
            <div key={pathStr} className="mb-2">
              <div className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                <button
                  onClick={() => togglePreviewFolderExpansion(pathStr)}
                  className="p-0.5 hover:bg-gray-100 rounded"
                  type="button"
                >
                  {hasChildren ? (
                    isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  ) : (
                    <div className="w-4" />
                  )}
                </button>
                <Folder size={16} className="text-yellow-500" />
                <span className="flex-1 font-medium">{folder.name}</span>
              </div>
              {isExpanded && hasChildren && (
                <div className="ml-6 mt-2">
                  {renderPreviewFolderStructure(folder.children, currentPath, level + 1)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Handle sort (only for Template name)
  const handleSort = (key, direction) => {
    if (key === 'name') {
      setSortBy(key);
      setSortOrder(direction);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Predefined Templates</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage folder structure templates for datarooms
        </p>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-col lg:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Templates</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="15">15 per page</option>
              <option value="30">30 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>

          <CreateButton onClick={handleCreateTemplate} size="sm">
            Add Template
          </CreateButton>
      </div>

      {/* Templates Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <EnhancedDataTable
          data={templates}
          columns={[
            {
              key: 'name',
              header: 'Template Name',
              sortable: true, // Only Template Name is sortable
              render: (value, row) => (
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-3" />
                  <div className="text-sm font-medium text-gray-900">
                    {row.name}
                  </div>
                </div>
              )
            },
            {
              key: 'description',
              header: 'Description',
              sortable: false,
              render: (value) => (
                <div className="text-sm text-gray-500">
                  {value || 'No description'}
                </div>
              )
            },
            {
              key: 'folder_count',
              header: 'Folders',
              sortable: false, // Removed sorting
              render: (value, row) => {
                const count = row.folder_counts ||  // From API response
                             row.folder_count ||
                             row.folders_count ||
                             row.total_folders ||
                             countFoldersRecursive(row.folders || row.folder_structure) ||
                             0;
                console.log('Folder count for row:', row.name, 'count:', count, 'folders:', row.folders, 'folder_structure:', row.folder_structure);
                return (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Folder className="w-3 h-3 mr-1" />
                    {count}
                  </span>
                );
              }
            },
            {
              key: 'is_active',
              header: 'Status',
              sortable: false, // Removed sorting
              render: (value, row) => (
                <Switch
                  checked={row.is_active}
                  onChange={() => handleToggleStatus(row)}
                  disabled={togglingStatus === row.id}
                  tooltip={row.is_active ? 'Click to deactivate' : 'Click to activate'}
                />
              )
            },
            {
              key: 'created_at',
              header: 'Created',
              sortable: false, // Removed sorting
              render: (value) => (
                <span className="text-sm text-gray-500">
                  {value ? new Date(value).toLocaleDateString() : '-'}
                </span>
              )
            }
          ]}
          actions={(row) => [
            {
              icon: Eye,
              label: 'Preview',
              onClick: () => handlePreviewTemplate(row)
            },
            {
              icon: Edit,
              label: 'Edit',
              onClick: () => handleEditTemplate(row)
            }
          ]}
          loading={loading}
          emptyMessage="No templates found"
          emptyIcon={Package}
          showColumnControls={true}
          enableColumnResize={true}
          enableDragDrop={true}
          onSort={handleSort}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:border-transparent"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:border-transparent"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * perPage, totalRecords)}
                  </span>{' '}
                  of <span className="font-medium">{totalRecords}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    const pageNum = idx + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-3 py-1.5 focus:border-transparent border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <ResponsiveModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
        }}
        title={showEditModal ? 'Edit Template' : 'Create Template'}
        size="large"
      >
        {saving ? (
          <FormLoader />
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full p-2 border rounded-lg ${formErrors.name ? 'border-red-500' : ''}`}
                placeholder="Enter template name"
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border rounded-lg"
                rows={3}
                placeholder="Enter template description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Status
              </label>
              <Switch
                checked={formData.is_active}
                onChange={(value) => setFormData({ ...formData, is_active: value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Folder Structure
              </label>
              <div className="border rounded-lg p-4">
                {/* Add Root Folder Section */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-300">
                    <Folder size={16} className="text-gray-500" />
                    <input
                      type="text"
                      value={newFolderInputs['root'] || ''}
                      onChange={(e) => updateNewFolderInput('root', e.target.value)}
                      placeholder="Add root folder"
                      className="flex-1 p-1 border rounded text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addRootFolder();
                        }
                      }}
                    />
                    <button
                      onClick={addRootFolder}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      type="button"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Folder Structure Display */}
                <div className="max-h-64 overflow-y-auto">
                  {folderStructure.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No folders added</p>
                  ) : (
                    renderImprovedFolderStructure(folderStructure)
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <CancelButton
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
              >
                Cancel
              </CancelButton>
              <SaveButton onClick={handleSaveTemplate}>
                {showEditModal ? 'Update' : 'Create'} Template
              </SaveButton>
            </div>
          </div>
        )}
      </ResponsiveModal>

      {/* Preview Modal */}
      <ResponsiveModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Template Preview"
        size="large"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600">Name</label>
              <p className="text-lg">{selectedTemplate.name}</p>
            </div>

            {selectedTemplate.description && (
              <div>
                <label className="block text-sm font-medium text-gray-600">Description</label>
                <p>{selectedTemplate.description}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-600">Status</label>
              <span className={`px-2 py-1 rounded-full text-sm ${
                selectedTemplate.is_active
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {selectedTemplate.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Folder Structure ({countFoldersRecursive(folderStructure)} folders)
              </label>
              <div className="border rounded-lg p-4 max-h-96 overflow-y-auto bg-gray-50">
                {folderStructure.length === 0 ? (
                  <p className="text-gray-500 text-center">No folders</p>
                ) : (
                  renderPreviewFolderStructure(folderStructure)
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <CancelButton onClick={() => setShowPreviewModal(false)}>
                Close
              </CancelButton>
            </div>
          </div>
        )}
      </ResponsiveModal>
    </div>
  );
};

export default PredefineTemplates;